import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { Observable, of, throwError } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { FacilityStore } from '../stores/facility.store';
import { Facility } from '../types/facility';
import { Language } from '../types/language';
import { MeasurementSystem } from '../types/measurement-system';
import { Profile } from '../types/profile';
import { UserPreferences } from '../types/user-preferences';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private userProfile: Profile | null = null;
  private _userPreferences?: UserPreferences;
  private measurementSystem: MeasurementSystem = MeasurementSystem.US_CUSTOMARY;
  private language: Language = Language.English;
  private facility?: Facility;

  get userPreferences(): UserPreferences | undefined {
    return this._userPreferences;
  }

  set userPreferences(preferences: UserPreferences | undefined) {
    this._userPreferences = preferences;
    this.updateMeasurementSystem();
    this.updateLanguage();
  }

  constructor(
    private http: HttpClient,
    private router: Router,
    private translocoService: TranslocoService,
    private facilityStore: FacilityStore,
  ) {
    this.facilityStore.currentFacility$.subscribe((facility) => {
      this.facility = facility;

      this.updateMeasurementSystem();
      this.updateLanguage();
    });
  }

  get profile(): Profile | null {
    return this.userProfile;
  }

  clearProfile() {
    this.userProfile = null;
  }

  /**
   * Returns user's entire profile
   */
  getProfile(useCache: boolean = true): Observable<Profile> {
    if (useCache && this.userProfile !== null) {
      return of(this.userProfile);
    }

    return this.http.get<Profile>(`${environment.apiDomain}/auth/users/profile`).pipe(
      map((profile: Profile) => {
        if (profile.locked) {
          this.userProfile = null;
          this.router.navigateByUrl('/locked');
          throwError(new Error('This account is locked.'));
        }

        this.userProfile = profile;
        return profile;
      }),
    );
  }

  /**
   * Updates a user's profile, email, first name, last name, etc.
   */
  updateProfile(profile: Profile): Observable<any> {
    return this.http.put(environment.apiDomain + '/auth/users', profile);
  }

  /**
   * Sends an object representing a password update, the user initiates
   *  this from within the account change profile page.
   * @param passwordChange any
   */
  changePassword(userUUID: string, passwordChange: { existingPassword: string; newPassword: string }): Observable<any> {
    return this.http.put(environment.apiDomain + `/auth/users/${userUUID}/password`, passwordChange);
  }

  /**
   * Sends single use token and new password to the api, after user
   * has followed link from email they requested via `sendResetPasswordRequest(email: string)`.
   * @param token string
   * @param newPassword string
   */
  resetPassword(token: string, newPassword: string): Observable<any> {
    return this.http.post(environment.apiDomain + '/auth/resetPassword', {
      token,
      password: newPassword,
    });
  }

  /**
   * Sends request to api, requesting a change password email to be sent
   *  to the user.
   * @param email string
   */
  sendResetPasswordRequest(email: string): Observable<any> {
    return this.http.post(environment.apiDomain + '/auth/sendResetPasswordEmail', {
      email,
    });
  }

  deleteAccount(profileId: string): Observable<any> {
    return this.http.delete(environment.apiDomain + `/auth/users/${profileId}`);
  }

  acknowledgeLegalNotice(profileId: string): Observable<HttpResponse<void>> {
    return this.http.patch<void>(
      environment.apiDomain + `/auth/users/${profileId}/acknowledge-legal-notice`,
      {},
      { observe: 'response' },
    );
  }

  getEmailSettings(): Observable<any> {
    return this.http.get<any>(environment.apiDomain + '/auth/users/email/settings');
  }

  changeEmailSettings(emailSettings: any): Observable<void> {
    return this.http.put<any>(environment.apiDomain + '/auth/users/email/settings', emailSettings);
  }

  getUserPreferences(userId?: string): Observable<UserPreferences | undefined> {
    if (this.userPreferences) {
      return of(this.userPreferences);
    }

    if (userId) {
      return this.http
        .get<UserPreferences | undefined>(`${environment.apiDomain}/ergonomics/user-preferences/${userId}`)
        .pipe(tap((preferences) => (this.userPreferences = preferences)));
    }

    return this.http.get<Profile>(`${environment.apiDomain}/auth/users/profile`).pipe(
      switchMap((profile: Profile) => {
        return this.http
          .get<UserPreferences | undefined>(`${environment.apiDomain}/ergonomics/user-preferences/${profile.uuid}`)
          .pipe(tap((preferences) => (this.userPreferences = preferences)));
      }),
    );
  }

  createUserPreferences(userPreferences: UserPreferences): Observable<any> {
    this.userPreferences = userPreferences;
    return this.http.post(environment.apiDomain + '/ergonomics/user-preferences', userPreferences);
  }

  updateUserPreferences(userPreferences: UserPreferences): Observable<any> {
    this.userPreferences = userPreferences;
    return this.http.put(environment.apiDomain + '/ergonomics/user-preferences', userPreferences);
  }

  getMeasurementSystem(): MeasurementSystem {
    return this.measurementSystem;
  }

  updateMeasurementSystem(): void {
    this.measurementSystem =
      this.userPreferences?.measurementSystem || this.facility?.measurementSystem || MeasurementSystem.US_CUSTOMARY;
  }

  getLanguage(): Language {
    return this.language;
  }

  updateLanguage(): void {
    this.language = this.userPreferences?.language || this.facility?.language || Language.English;
    this.changeLanguage(this.language);
  }

  changeLanguage(language: Language): void {
    if (language && this.translocoService.getActiveLang() !== language) {
      this.translocoService.setActiveLang(language);
    }
  }
}
